import React, { createContext, useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import { useTheme } from "@material-ui/core/styles";
import api from "../../services/api";
import useWhatsApps from "../../hooks/useWhatsApps";
import usePlans from "../../hooks/usePlans";
import WebphoneFab from "../../components/WebphoneFab";
import GalleraVoiceCall from "../../components/GalleraVoiceCall";
import GalleraVoiceFab from "../../components/GalleraVoiceFab";
// PONTE DE VOZ OFICIAL (porte 7.8.1) — convive com a NOSSA voz.
import MultiflowVozFab from "../../components/MultiflowVozFab";
import MultiflowVozWidget from "../../components/MultiflowVozWidget";
import VoiceChannelPicker from "../../components/VoiceChannelPicker";
import {
  openMultiflowVozSoftphone,
  toggleMultiflowVozSoftphone
} from "../../utils/voiceSoftphone";
import { AuthContext } from "../Auth/AuthContext";

const WAVOIP_CDN = "https://cdn.jsdelivr.net/npm/@wavoip/wavoip-webphone/dist/index.umd.min.js";

const WhatsAppsContext = createContext();

/** Lembra a ultima conexao de voz escolhida, por empresa. */
function voiceChannelStorageKey(companyId) {
  return `mfv_voice_channel_${companyId || "0"}`;
}

/** Token do HUB comeca com "mfv_". Qualquer outro = Wavoip (a nossa voz). */
function isMfv(token) {
  return String(token || "").toLowerCase().startsWith("mfv_");
}

const WhatsAppsProvider = ({ children }) => {
  const theme = useTheme();
  const whatsAppData = useWhatsApps();
  const { loading = false, whatsApps = [] } = whatsAppData || {};
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const wavoipRenderDoneRef = useRef(false);

  const [wavoipToken, setWavoipToken] = useState(null);
  const [wavoipEnabled, setWavoipEnabled] = useState(false);
  // PONTE DE VOZ OFICIAL — canais mfv_ do HUB
  const [voiceChannels, setVoiceChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerResolve, setPickerResolve] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPlanConfig = async () => {
      try {
        if (user?.companyId) {
          const planConfigs = await getPlanCompany(undefined, user.companyId);
          setWavoipEnabled(planConfigs?.plan?.wavoip || false);
        }
      } catch (err) {
        console.error("Erro ao buscar configuração do plano:", err);
        setWavoipEnabled(false);
      }
    };
    fetchPlanConfig();
  }, [user?.companyId, getPlanCompany]);

  useEffect(() => {
    // ✅ CORREÇÃO: Só fazer requisição se o usuário estiver autenticado
    if (!user?.id || !user?.companyId) {
      setLoadingSession(false);
      return;
    }

    const fetchSession = async () => {
      try {
        const { data } = await api.get("/call/historical/user/whatsapp");

        // let wavoipToken  = "";
        // for(const d of data){
        //   if(d?.wavoip){
        //     wavoipToken = d.wavoip;
        //     break;
        //   }
        // }
        // PONTE DE VOZ OFICIAL: o backend passou a devolver `channels`. Se vier,
        // guardamos para o seletor de canal do HUB. Se NAO vier (backend antigo),
        // cai no comportamento de sempre — a nossa voz nao muda em nada.
        const channels = Array.isArray(data?.channels) ? data.channels : [];
        setVoiceChannels(channels);

        let preferredId = null;
        try {
          preferredId =
            Number(localStorage.getItem(voiceChannelStorageKey(user.companyId))) || null;
        } catch {
          preferredId = null;
        }
        const preferred =
          channels.find((c) => Number(c.id) === Number(preferredId)) || channels[0] || null;

        if (channels.length === 1) {
          setSelectedChannelId(channels[0].id);
          setWavoipToken(channels[0].wavoip || null);
        } else if (preferred) {
          setSelectedChannelId(preferred.id);
          setWavoipToken(preferred.wavoip || null);
        } else {
          setSelectedChannelId(data?.whatsapp?.id || null);
          setWavoipToken(data?.whatsapp?.wavoip || null);
        }
      } catch (err) {
        // ✅ CORREÇÃO: Não logar erro se for 401 (não autenticado)
        if (err?.response?.status !== 401) {
          console.error("Erro fetchSession:", err);
        }
        setWavoipToken(null);
        setVoiceChannels([]);
      } finally {
        setLoadingSession(false);
      }
    };
    fetchSession();
  }, [user?.id, user?.companyId]);

  /**
   * Carrega o widget oficial WAVoIP (window.wavoip) logo após o login quando o plano
   * tem wavoip habilitado. Assim o sistema pode receber chamadas e tocar mesmo com
   * o widget fechado; o usuário abre pelo FAB quando quiser.
   */
  // 1) Assim que o usuário está logado e o plano tem wavoip, carrega o script e faz render()
  useEffect(() => {
    if (!user?.id || !wavoipEnabled) return;

    const loadAndRenderWidget = async () => {
      if (!window.wavoipWebphone) {
        await new Promise((resolve, reject) => {
          if (document.querySelector(`script[src="${WAVOIP_CDN}"]`)) {
            if (window.wavoipWebphone) return resolve();
            const check = setInterval(() => {
              if (window.wavoipWebphone) {
                clearInterval(check);
                resolve();
              }
            }, 100);
            return;
          }
          const script = document.createElement("script");
          script.src = WAVOIP_CDN;
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error("Falha ao carregar widget WAVoIP"));
          document.head.appendChild(script);
        });
      }
      if (!wavoipRenderDoneRef.current && window.wavoipWebphone) {
        await window.wavoipWebphone.render({
          position: "bottom-right",
          settingsMenu: {
            showSettingsButton: false,
            showWidgetButton: false,
          },
        });
        wavoipRenderDoneRef.current = true;
      }
      if (window.wavoip) {
        window.wavoip.settings.setShowSettings(false);
        window.wavoip.settings.setShowWidgetButton(false);
        const mode = theme?.palette?.type ?? theme?.palette?.mode ?? "light";
        window.wavoip.theme.setTheme(mode === "dark" ? "dark" : "light");
      }
    };

    loadAndRenderWidget().catch((err) =>
      console.error("Erro ao carregar widget WAVoIP:", err)
    );
  }, [user?.id, wavoipEnabled]);

  // 2) Assim que o token estiver disponível, registra o dispositivo para receber chamadas (tocar)
  useEffect(() => {
    if (!wavoipToken) return;
    // ⚠️ FIX gallera: NUNCA registrar token do HUB (mfv_) no SDK do Wavoip. São
    // sistemas diferentes — o mfv_ é do Multiflow Voz e não vale nada aqui. Sem
    // esta guarda, escolher um canal Oficial injetava o token errado no SDK.
    if (isMfv(wavoipToken)) return;
    if (!window.wavoip) {
      const retry = setInterval(() => {
        if (window.wavoip) {
          clearInterval(retry);
          window.wavoip.settings.setShowSettings(false);
          window.wavoip.device.addDevice(wavoipToken);
          window.wavoip.settings.setShowWidgetButton(false);
        }
      }, 200);
      return () => clearInterval(retry);
    }
    window.wavoip.settings.setShowSettings(false);
    window.wavoip.device.addDevice(wavoipToken);
    window.wavoip.settings.setShowWidgetButton(false);
  }, [wavoipToken]);


  // ============ PONTE DE VOZ OFICIAL (HUB) — selecao de canal ============
  // Só age em canais com token mfv_. Sem canal mfv_, tudo abaixo fica inerte e
  // a nossa voz (Wavoip/GalleraVoice) segue exatamente como sempre foi.
  const mfvChannels = useMemo(
    () => voiceChannels.filter((c) => c.isMfv || isMfv(c.wavoip)),
    [voiceChannels]
  );
  const hasMfv = mfvChannels.length > 0;
  const isMfvToken = isMfv(wavoipToken);
  const selectedMfvChannel = useMemo(
    () => mfvChannels.find((c) => Number(c.id) === Number(selectedChannelId)) || null,
    [mfvChannels, selectedChannelId]
  );

  const selectChannel = useCallback(
    (channel) => {
      if (!channel) return;
      setSelectedChannelId(channel.id);
      setWavoipToken(channel.wavoip || null);
      try {
        localStorage.setItem(voiceChannelStorageKey(user?.companyId), String(channel.id));
      } catch {
        /* ignore */
      }
    },
    [user?.companyId]
  );

  const requestVoiceChannelPick = useCallback(() => {
    const list = mfvChannels.length ? mfvChannels : voiceChannels;
    if (list.length <= 1) {
      const only = list[0] || null;
      if (only) selectChannel(only);
      return Promise.resolve(only);
    }
    return new Promise((resolve) => {
      setPickerResolve(() => resolve);
      setPickerOpen(true);
    });
  }, [mfvChannels, voiceChannels, selectChannel]);

  const toggleSoftphoneWithChannelPick = useCallback(async () => {
    if (mfvChannels.length > 1) {
      const channel = await requestVoiceChannelPick();
      if (!channel?.wavoip) return;
      selectChannel(channel);
      toggleMultiflowVozSoftphone({ token: channel.wavoip });
      return;
    }
    const token = mfvChannels[0]?.wavoip || wavoipToken;
    if (token) toggleMultiflowVozSoftphone({ token });
  }, [mfvChannels, requestVoiceChannelPick, selectChannel, wavoipToken]);

  const openSoftphoneWithChannelPick = useCallback(
    async ({ phone = "", name = "", startIfReady = false, forcePick = false } = {}) => {
      let channel = null;
      if (forcePick || mfvChannels.length > 1) {
        channel = await requestVoiceChannelPick();
      } else {
        channel = mfvChannels[0] || null;
      }
      if (!channel?.wavoip) {
        throw new Error("Nenhum canal de voz disponível para o seu usuário.");
      }
      selectChannel(channel);
      return openMultiflowVozSoftphone({
        token: channel.wavoip,
        phone,
        name,
        startIfReady
      });
    },
    [mfvChannels, requestVoiceChannelPick, selectChannel]
  );

  // Log error state for debugging
  if (error) {
    console.warn("WhatsAppsProvider error:", error);
  }

  return (
    <WhatsAppsContext.Provider
      value={{
        whatsApps,
        loading,
        error,
        // PONTE DE VOZ OFICIAL — quem quiser discar pelo HUB usa isto
        voiceChannels,
        mfvChannels,
        selectedMfvChannel,
        selectChannel,
        openSoftphoneWithChannelPick,
        toggleSoftphoneWithChannelPick
      }}
    >
      {children}
      {/* NOSSA voz (Wavoip): aparece quando o token selecionado NAO e mfv_.
          ⚠️ FIX gallera: o upstream usava `&& !hasMfv`, que e POR EMPRESA — bastava
          UMA conexao mfv_ na empresa para TODO MUNDO perder o botao do Wavoip,
          inclusive quem so usa QR/Baileys. Empresa mista (uma conexao Oficial com
          HUB + varias Baileys com Wavoip) e o caso normal aqui, entao a decisao
          tem que ser POR CANAL SELECIONADO, nao por empresa. */}
      {wavoipEnabled && wavoipToken && !isMfvToken && <WebphoneFab />}
      {/* gallera-voice FASE 2: overlay global de ligações (auto-gated por /voice/enabled).
          Suporta várias chamadas simultâneas empilhadas no canto. */}
      <GalleraVoiceCall />
      {/* gallera-voice: botão flutuante (estilo Wavoip) p/ ORIGINAR chamadas.
          Auto-gated por /voice/enabled; a chamada em si é tratada pelo overlay acima. */}
      <GalleraVoiceFab />

      {/* ===== PONTE DE VOZ OFICIAL (HUB) — só aparece se houver canal mfv_ ===== */}
      {wavoipEnabled && hasMfv && (
        <>
          <MultiflowVozFab
            token={wavoipToken}
            onOpen={toggleSoftphoneWithChannelPick}
            channelsCount={mfvChannels.length}
            channelName={selectedMfvChannel?.name}
          />
          <MultiflowVozWidget
            token={wavoipToken}
            whatsappId={selectedMfvChannel?.id || null}
            currentUserId={user?.id || null}
            channels={mfvChannels}
            onSwitchChannel={selectChannel}
          />
        </>
      )}
      <VoiceChannelPicker
        open={pickerOpen}
        channels={mfvChannels.length ? mfvChannels : voiceChannels}
        selectedId={selectedChannelId}
        onClose={() => {
          setPickerOpen(false);
          if (pickerResolve) {
            pickerResolve(null);
            setPickerResolve(null);
          }
        }}
        onSelect={(channel) => {
          setPickerOpen(false);
          selectChannel(channel);
          if (pickerResolve) {
            pickerResolve(channel);
            setPickerResolve(null);
          }
        }}
      />
    </WhatsAppsContext.Provider>
  );
};

export { WhatsAppsContext, WhatsAppsProvider };